import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, map, of, switchMap, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { CurrentProductService } from '@spartacus/storefront';
import { ImageType, Product } from '@spartacus/core';

import {
  BrandThemesEnum,
  ComponentWrapperWidthEnum,
  IExtendedLink,
  ILinkMedia,
  ImageRatioEnum,
  IMedia,
  MarginEnum,
  PositionEnum,
  SdSpyTargetDirective,
  ThemesEnum
} from '@seb/web-design-system/core';

import { SdWysiwygComponent, SdWysiwygParagraphComponent } from '@seb/web-design-system/wysiwyg';
import { SdTipsComponent } from '@seb/web-design-system/tips';
import { ActivatedRoute } from '@angular/router';

import {
  Bundle,
  CmsLinkDSComponentInterface,
  WysiwygComponentsObjectsInterface,
  WysiwygModuleComponentInterface
} from '../../../models';

import {
  BrandCornerMapper,
  ImageRatioMapper,
  LinkMapper,
  ThemesMapper,
  transformHeadlineLevelToTitleLevel
} from '../../../mappers';

import { BundleService, ChatService, ImageService, TrackingService, WhatsInTheBoxService } from '../../../services';
import { UppercaseBrandThemesEnum, WysiwygLayoutEnum } from '../../../enums';
import { AbstractModuleComponent } from '../abstract-module.component';
import { WrapperComponent } from '../../../components/static/core';

@Component({
  selector: 'wc-wysiwyg',
  templateUrl: './wysiwyg.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    WrapperComponent,
    SdWysiwygComponent,
    SdWysiwygParagraphComponent,
    SdTipsComponent,
    SdSpyTargetDirective
  ]
})
export class WysiwygModuleComponent extends AbstractModuleComponent<WysiwygModuleComponentInterface> implements OnInit, OnDestroy {
  isPdp = false;
  isProductOrBundle = false;
  textConfig: PositionEnum.top | PositionEnum.center;
  mediaConfig: PositionEnum.left | PositionEnum.right;
  icon = 'icon-brand-xs';
  links: ILinkMedia[];
  medias: IMedia[] = [{}];
  title = 'title';
  titleLevel: number;
  content = 'content';
  layout = '';
  wysiwygComponents: WysiwygComponentsObjectsInterface[] = [{ component: { title: 'title', content: 'content' } }];
  firstTips: number;
  themeWyziwyg: ThemesEnum.dark | BrandThemesEnum.brand = BrandThemesEnum.brand;
  wrapperBackgroundTheme: ThemesEnum | BrandThemesEnum;
  mediaTop: IMedia;
  mediaBottom: IMedia;
  ratioMediaTop: ImageRatioEnum;
  ratioMediaBottom: ImageRatioEnum;
  componentWidth: ComponentWrapperWidthEnum;
  marginSize: MarginEnum.default_margin | MarginEnum.without_margin = MarginEnum.default_margin;

  constructor(
    protected readonly route: ActivatedRoute,
    private readonly currentProductService: CurrentProductService,
    private readonly imageService: ImageService,
    private readonly translateService: TranslateService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly bundleService: BundleService,
    private readonly trackingService: TrackingService,
    private readonly chatService: ChatService,
    private readonly brandCornerMapper: BrandCornerMapper,
    private readonly whatsInTheBoxService: WhatsInTheBoxService
  ) {
    super();
  }

  override onModuleInit(module: WysiwygModuleComponentInterface): void {
    this.mapLayout(module.layout);
    this.cdRef.markForCheck();
  }

  ngOnInit(): void {
    const component$ = this.module$.pipe(
      this.componentService.reloadComponentForArrayProperties(['medias']),
      switchMap((cmp: WysiwygModuleComponentInterface) =>
        cmp.links?.length > 0 ? this.componentService.getLazyComponentData(cmp, 'links') : of(cmp)
      )
    );
    combineLatest([this.currentProductService.getProduct(), component$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([product, cmp]: [Product, WysiwygModuleComponentInterface]) => {
        this.wrapperBackgroundTheme = this.wrapper?.wrapperBackgroundTheme
          ? ThemesMapper.mapBrandThemeOrDefault(
            this.wrapper?.wrapperBackgroundTheme as UppercaseBrandThemesEnum,
            ThemesEnum.light
          )
          : ThemesEnum.light;
        this.themeWyziwyg =
          this.wrapper?.wrapperBackgroundTheme === ThemesEnum.light
            ? this.brandCornerMapper.toWcConfig(this.brandCorner).isThemeDark
              ? ThemesEnum.dark
              : BrandThemesEnum.brand
            : null;
        this.route?.queryParamMap
          ?.pipe(
            map((param) => param.get('bundle')),
            switchMap((bundleCode) => (bundleCode ? this.bundleService.getBundle(bundleCode) : of(undefined))),
            takeUntil(this.destroy$)
          )
          .subscribe((bundle: Bundle): void => {
            if (bundle) {
              this.toBundleWysiwyg(bundle, cmp);
            } else if (product) {
              this.toProductWysiwyg(product, cmp);
            } else {
              this.toStandardWysiwyg(cmp);
            }
            this.setLoaded();
            this.cdRef.markForCheck();
          });
      });
  }

  onLinkClick(link: ILinkMedia): void {
    if (link?.url === '<<chat>>') {
      this.chatService.clickOpenChat();
    }
    this.trackingService.sendEvent('clickdownloadApp', {
      appType: link.linkName
    });
  }

  onMediaPlay(event: any): void {
    this.trackingService.sendEvent('clickVideo', {
      videoZone: event.title,
      videoAction: 'Play',
      videoName: event.videoName
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private toBundleWysiwyg(bundle: Bundle, cmp: WysiwygModuleComponentInterface) {
    this.isPdp = false;
    this.isProductOrBundle = true;
    this.title = this.translateService.instant('pdp.sectionTitles.whatsInTheBox', {
      productName: bundle.name
    });
    this.titleLevel = transformHeadlineLevelToTitleLevel(cmp.titleLevel);
    this.content = bundle.description;
    const media = bundle.galleryImages?.[ImageType.GALLERY]?.[0].main;
    this.mediaTop = media ? media : null;
    this.links = this.toButtons(cmp.linksObjects);
    this.componentWidth = ComponentWrapperWidthEnum.absoluteFullWidth;
    this.marginSize = MarginEnum.without_margin;
  }

  private toProductWysiwyg(product: Product, cmp: WysiwygModuleComponentInterface) {
    this.isPdp = true;
    this.isProductOrBundle = true;
    this.title = this.translateService.instant('pdp.sectionTitles.whatsInTheBox', {
      productName: product.name
    });
    this.layout = 'vertical';
    this.titleLevel = transformHeadlineLevelToTitleLevel(cmp.titleLevel);
    this.content = product.whatIsInBox?.detail;
    const media =
      product.whatIsInBox?.image?.find((image) => image.format === 'extra') ||
      product.whatIsInBox?.image?.find((image) => image.format === 'main');
    this.mediaTop = media ? this.imageService.normalizeMedia(media) : null;
    this.links = this.toButtons(cmp.linksObjects);
    this.componentWidth = ComponentWrapperWidthEnum.absoluteFullWidth;
    this.marginSize = MarginEnum.without_margin;

    this.whatsInTheBoxService.dispatchHasContent(!!this.content);
  }

  private toStandardWysiwyg(cmp: WysiwygModuleComponentInterface) {
    this.wysiwygComponents = cmp.componentsObjects?.map((cmpObj, i) => {
      const isTips = JSON.parse(cmpObj.isTips);
      if (isTips) {
        return {
          component: cmpObj,
          isTips,
          index: i
        };
      } else {
        return {
          component: cmpObj,
          isTips
        };
      }
    });

    this.firstTips = this.wysiwygComponents.findIndex((component) => component.isTips);
    this.isPdp = false;
    this.isProductOrBundle = false;
    this.title = cmp.headline;
    this.titleLevel = transformHeadlineLevelToTitleLevel(cmp.titleLevel);

    this.content = '';
    this.links = this.toButtons(cmp.linksObjects);
    this.mediaTop = this.imageService.normalizeMedia(cmp.mediaTop?.media);
    this.mediaBottom = this.imageService.normalizeMedia(cmp.mediaBottom?.media);
    this.ratioMediaTop = ImageRatioMapper.mapImageRatio(cmp.mediaTop?.mediaRatio);
    this.ratioMediaBottom = ImageRatioMapper.mapImageRatio(cmp.mediaBottom?.mediaRatio);
  }

  private mapLayout(layout: WysiwygLayoutEnum): void {
    switch (layout) {
      case WysiwygLayoutEnum.TEXT_CENTERLEFT_MEDIA_RIGHT:
        this.textConfig = PositionEnum.center;
        this.mediaConfig = PositionEnum.right;
        break;
      case WysiwygLayoutEnum.TEXT_CENTERRIGHT_MEDIA_LEFT:
        this.textConfig = PositionEnum.center;
        this.mediaConfig = PositionEnum.left;
        break;
      case WysiwygLayoutEnum.TEXT_TOPLEFT_MEDIA_RIGHT:
        this.textConfig = PositionEnum.top;
        this.mediaConfig = PositionEnum.right;
        break;
      case WysiwygLayoutEnum.TEXT_TOPRIGHT_MEDIA_LEFT:
        this.textConfig = PositionEnum.top;
        this.mediaConfig = PositionEnum.left;
        break;
      default:
        this.textConfig = PositionEnum.center;
        this.mediaConfig = PositionEnum.left;
    }
  }

  private toButtons(links: CmsLinkDSComponentInterface[]): IExtendedLink[] {
    if (!!links && links.length > 0) {
      return links.map((link) => ({
        ...LinkMapper.cmsLinkToILink(link),
        media: this.imageService.normalizeMedia(link.media)
      }));
    } else {
      return [];
    }
  }

  protected readonly transformHeadlineLevelToTitleLevel = transformHeadlineLevelToTitleLevel;
}
